import React, { ReactNode, useCallback, useMemo } from "react";
import { ClassWithChildren, getClassPath } from "@helpers/class";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { buildClassParentTree } from "./buildClassParentTree";
import styles from "./ClassTreeSelectField.module.scss";

interface Props {
    value: string | null;
    onChange: (value: string | null) => void;
    classesWithChildren: ClassWithChildren[];
    classesMap: Map<string, ClassWithChildren>;
    placeholder?: string;
}

export const ClassTreeSelectField: React.FC<Props> = ({
    value,
    onChange,
    classesWithChildren,
    classesMap,
    placeholder,
}) => {
    const treeItems = useMemo(
        () => buildClassParentTree({ classesWithChildren }),
        [classesWithChildren],
    );

    const renderTriggerLabel = useCallback(
        (selectedValues: readonly string[]): ReactNode => {
            const [selectedValue] = selectedValues;
            const selectedClass = classesMap.get(selectedValue);
            if (!selectedClass || selectedClass.depth === 0) {
                return undefined;
            }
            const fullPath = getClassPath({ classesMap, id: selectedValue });
            return (
                <>
                    <span className={styles.leaf}>{selectedClass.label}</span>
                    <Tooltip content={fullPath}>
                        <span className={styles.nestedIndicator}>
                            <Tag color="primary" size="sm">
                                Nested
                            </Tag>
                        </span>
                    </Tooltip>
                </>
            );
        },
        [classesMap],
    );

    return (
        <Picker.Select
            items={treeItems}
            value={value}
            onValueChange={onChange}
            searchable
            autoHighlight
            defaultExpandAll
            selectableParents
            clearable
            placeholder={placeholder}
            renderValue={renderTriggerLabel}
            trigger={<Picker.Button variant="primary" size="xl" block />}
        />
    );
};
